import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ORDER_STATUSES, ORDER_STATUS_LABELS, statusStyle, formatOrderRef, formatCHF } from "@/lib/orderStatus";
import { ChevronDown, ChevronUp, Filter, AlertTriangle, Check, FileDown, CalendarDays, Printer, Archive, ArchiveRestore } from "lucide-react";
import { ReviewDisplay } from "@/components/OrderReviewModal";
import { ordersToCsv, downloadCsv } from "@/lib/csvExport";
import { generateOrderPdf } from "@/lib/orderPdf";
import BulkActionBar, { SelectCheckbox } from "@/components/BulkActionBar";

function StatusPill({ status }) {
    const st = statusStyle(status);
    return (
        <span
            className="text-[10px] uppercase tracking-widest px-2.5 py-1 border font-medium whitespace-nowrap"
            style={{ color: st.color, background: st.background || "transparent", borderColor: st.border }}
        >
            {ORDER_STATUS_LABELS[status] || status}
        </span>
    );
}

export default function AdminCommandes() {
    const qc = useQueryClient();
    // Default view: only orders that need action ("à traiter").
    const [filter, setFilter] = useState("to_process");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // The filter drives whether we want archived orders or not.
    //   "archived"     -> only archived   (archived=true)
    //   any other      -> only non-archived (default)
    const archivedParam = filter === "archived" ? "true" : "false";
    // For "archived" view, do not restrict status.
    const statusParam = filter === "archived" ? "all" : filter;

    const { data: allOrders = [], isLoading } = useQuery({
        queryKey: ["admin-orders", statusParam, archivedParam],
        queryFn: async () => {
            const params = { archived: archivedParam };
            if (statusParam && statusParam !== "all") params.status_filter = statusParam;
            return (await api.get("/admin/orders", { params })).data;
        },
        refetchInterval: 30_000,
    });

    // Client-side date range filter (applied on top of the server-side status filter).
    const orders = useMemo(() => {
        if (!from && !to) return allOrders;
        const fromMs = from ? new Date(from + "T00:00:00").getTime() : -Infinity;
        // Include the whole "to" day (up to 23:59:59.999)
        const toMs = to ? new Date(to + "T23:59:59.999").getTime() : Infinity;
        return allOrders.filter((o) => {
            const t = new Date(o.created_at).getTime();
            return t >= fromMs && t <= toMs;
        });
    }, [allOrders, from, to]);

    const applyMonth = (offset = 0) => {
        // offset 0 = current month, -1 = previous month
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
        const iso = (d) => d.toISOString().slice(0, 10);
        setFrom(iso(first));
        setTo(iso(last));
    };
    const clearRange = () => {
        setFrom("");
        setTo("");
    };

    const updateStatus = useMutation({
        mutationFn: ({ id, status }) => api.post(`/admin/orders/${id}/status`, { status }),
        onSuccess: () => {
            toast.success("Statut mis à jour — client notifié par e-mail");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const resolveComplaint = useMutation({
        mutationFn: ({ id, note }) => api.post(`/admin/orders/${id}/review/resolve`, { resolution_note: note || null }),
        onSuccess: () => {
            toast.success("Réclamation marquée comme résolue");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const archive = useMutation({
        mutationFn: (id) => api.post(`/admin/orders/${id}/archive`),
        onSuccess: () => {
            toast.success("Commande archivée");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const unarchive = useMutation({
        mutationFn: (id) => api.post(`/admin/orders/${id}/unarchive`),
        onSuccess: () => {
            toast.success("Commande désarchivée");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const bulkArchive = useMutation({
        mutationFn: (ids) => api.post("/admin/orders/bulk-archive", { ids }),
        onSuccess: (res) => {
            toast.success(`${res.data?.archived ?? 0} commande(s) archivée(s)`);
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const bulkUnarchive = useMutation({
        mutationFn: (ids) => api.post("/admin/orders/bulk-unarchive", { ids }),
        onSuccess: (res) => {
            toast.success(`${res.data?.unarchived ?? 0} commande(s) désarchivée(s)`);
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };
    const allSelected = orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
    const toggleAll = () => {
        if (allSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(orders.map((o) => o.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const isArchivedView = filter === "archived";
    const onBulkAction = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const verb = isArchivedView ? "désarchiver" : "archiver";
        if (window.confirm(`Voulez-vous ${verb} ${ids.length} commande${ids.length > 1 ? "s" : ""} ?`)) {
            (isArchivedView ? bulkUnarchive : bulkArchive).mutate(ids);
        }
    };

    const exportCsv = () => {
        if (!orders || orders.length === 0) {
            toast.error("Aucune commande à exporter avec les filtres actuels.");
            return;
        }
        const csv = ordersToCsv(orders);
        // Build filename with status + date range
        const parts = ["boucherie-costa-commandes"];
        if (filter && filter !== "all") parts.push(filter);
        if (from) parts.push(`du-${from}`);
        if (to) parts.push(`au-${to}`);
        if (!from && !to) parts.push(new Date().toISOString().slice(0, 10));
        const filename = `${parts.join("-")}.csv`;
        downloadCsv(filename, csv);
        toast.success(`${orders.length} commande${orders.length > 1 ? "s" : ""} exportée${orders.length > 1 ? "s" : ""} en CSV`);
    };

    const printOrder = (o) => {
        try {
            generateOrderPdf(o);
        } catch (e) {
            toast.error("Impossible de générer le PDF : " + (e?.message || "erreur"));
        }
    };

    return (
        <div data-testid="admin-orders">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Commandes</h2>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        Suivez et faites évoluer le statut de chaque commande — le client est notifié par e-mail.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="flex items-center gap-2">
                        <Filter size={14} color="#D4BC7C" />
                        <select
                            className="bc-input"
                            style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
                            value={filter}
                            onChange={(e) => { setFilter(e.target.value); setSelectedIds(new Set()); }}
                            data-testid="orders-filter"
                        >
                            <option value="to_process">À traiter</option>
                            <option value="all">Tous (non archivés)</option>
                            {ORDER_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                    {ORDER_STATUS_LABELS[s]}
                                </option>
                            ))}
                            <option value="archived">📥 Archivées</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <CalendarDays size={14} color="#D4BC7C" />
                        <input
                            type="date"
                            className="bc-input"
                            style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem", width: 160 }}
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            aria-label="Date de début"
                            data-testid="orders-date-from"
                        />
                        <span className="text-[#A0A0A0] text-xs">→</span>
                        <input
                            type="date"
                            className="bc-input"
                            style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem", width: 160 }}
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            aria-label="Date de fin"
                            data-testid="orders-date-to"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm bc-btn--ghost"
                            onClick={() => applyMonth(0)}
                            title="Filtrer sur le mois en cours"
                            data-testid="orders-month-current"
                        >
                            Ce mois
                        </button>
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm bc-btn--ghost"
                            onClick={() => applyMonth(-1)}
                            title="Filtrer sur le mois précédent"
                            data-testid="orders-month-previous"
                        >
                            Mois précédent
                        </button>
                        {(from || to) && (
                            <button
                                type="button"
                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                onClick={clearRange}
                                data-testid="orders-clear-range"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        className="bc-btn bc-btn--sm"
                        onClick={exportCsv}
                        disabled={!orders || orders.length === 0}
                        title="Télécharger les commandes affichées au format CSV (Excel)"
                        data-testid="orders-export-csv"
                    >
                        <FileDown size={14} /> Exporter CSV
                        {orders.length > 0 && (
                            <span className="ml-1 text-[10px] text-[#000]/70">({orders.length})</span>
                        )}
                    </button>
                </div>
            </div>

            {isLoading && <p className="text-[#A0A0A0] mt-8">Chargement...</p>}

            {!isLoading && orders.length === 0 && (
                <div className="bc-card text-center py-14 mt-8" data-testid="orders-empty">
                    <p className="font-serif text-[#D4BC7C] text-2xl">Aucune commande</p>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        {filter === "to_process"
                            ? "Aucune commande à traiter pour le moment."
                            : filter === "archived"
                                ? "Aucune commande archivée."
                                : "Aucune commande reçue avec ces filtres."}
                    </p>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div
                    className="sticky top-2 z-30 border p-3 flex items-center justify-between gap-3 flex-wrap mt-6"
                    style={{
                        borderColor: "var(--bc-gold)",
                        background: "rgba(15,15,15,0.95)",
                        backdropFilter: "blur(8px)",
                    }}
                    data-testid="orders-bulk-bar"
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-[#D4BC7C] font-medium">
                            {selectedIds.size} commande{selectedIds.size > 1 ? "s" : ""} sélectionnée{selectedIds.size > 1 ? "s" : ""}
                        </span>
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="text-xs text-[#F2ECE0] hover:text-[#D4BC7C]"
                            data-testid="orders-bulk-toggle-all"
                        >
                            {allSelected ? "Tout désélectionner" : `Tout sélectionner (${orders.length})`}
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm bc-btn--ghost"
                            onClick={clearSelection}
                            data-testid="orders-bulk-clear"
                        >
                            Effacer
                        </button>
                        <button
                            type="button"
                            className="bc-btn bc-btn--sm"
                            onClick={onBulkAction}
                            disabled={bulkArchive.isPending || bulkUnarchive.isPending}
                            data-testid="orders-bulk-archive"
                        >
                            {isArchivedView ? (
                                <>
                                    <ArchiveRestore size={14} /> Désarchiver la sélection
                                </>
                            ) : (
                                <>
                                    <Archive size={14} /> Archiver la sélection
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-6 space-y-4">
                {orders.map((o) => {
                    const open = expanded === o.id;
                    const isArchived = !!o.archived;
                    return (
                        <div
                            key={o.id}
                            className="bc-card"
                            data-testid={`admin-order-${o.id}`}
                            style={isArchived ? { opacity: 0.75 } : undefined}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="pt-1 shrink-0">
                                    <SelectCheckbox
                                        checked={selectedIds.has(o.id)}
                                        onChange={(v) => toggleSelect(o.id, v)}
                                        testId={`select-order-admin-${o.id}`}
                                    />
                                </div>
                                <div
                                    className="flex-1 min-w-0 cursor-pointer"
                                    onClick={() => setExpanded(open ? null : o.id)}
                                >
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-serif text-[#D4BC7C] text-lg">{formatOrderRef(o.id)}</span>
                                        <StatusPill status={o.status} />
                                        {isArchived && (
                                            <span
                                                className="text-[10px] uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1"
                                                style={{ color: "#A0A0A0", borderColor: "var(--bc-border)" }}
                                                data-testid={`badge-archived-${o.id}`}
                                            >
                                                <Archive size={10} /> Archivée
                                            </span>
                                        )}
                                        {o.review && (
                                            <span data-testid={`admin-order-review-badge-${o.id}`}>
                                                <ReviewDisplay review={o.review} compact />
                                            </span>
                                        )}
                                        {o.review?.has_complaint && o.review?.complaint_status !== "resolved" && (
                                            <span
                                                className="text-[10px] uppercase tracking-widest px-2 py-1 border font-medium inline-flex items-center gap-1"
                                                style={{ color: "#fca5a5", borderColor: "#fca5a5", background: "rgba(252,165,165,0.08)" }}
                                                data-testid={`admin-complaint-open-${o.id}`}
                                            >
                                                <AlertTriangle size={10} /> Réclamation
                                            </span>
                                        )}
                                        <span className="text-[10px] uppercase tracking-widest text-[#A0A0A0]">
                                            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        </span>
                                    </div>
                                    <p className="text-sm text-[#F2ECE0] mt-2">
                                        <span className="text-[#D4BC7C]">{o.user_raison_sociale}</span>
                                        <span className="text-[#A0A0A0]"> · {o.user_prenom} {o.user_nom}</span>
                                    </p>
                                    <p className="text-xs text-[#A0A0A0] mt-1">
                                        {new Date(o.created_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}
                                        {" · "}{o.items.length} article{o.items.length > 1 ? "s" : ""}
                                        {o.delivery_date && (
                                            <> · Livraison : {new Date(o.delivery_date).toLocaleDateString("fr-CH")}</>
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-col items-end gap-3">
                                    <p className="font-serif text-[#D4BC7C] text-2xl">{formatCHF(o.total_ht)}</p>
                                    <div className="flex items-center gap-2 flex-wrap justify-end">
                                        <button
                                            type="button"
                                            className="bc-btn bc-btn--sm bc-btn--ghost"
                                            onClick={(e) => { e.stopPropagation(); printOrder(o); }}
                                            title="Générer le bon de commande PDF"
                                            data-testid={`print-order-${o.id}`}
                                        >
                                            <Printer size={14} /> Imprimer
                                        </button>
                                        {isArchived ? (
                                            <button
                                                type="button"
                                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                                onClick={(e) => { e.stopPropagation(); unarchive.mutate(o.id); }}
                                                title="Désarchiver cette commande"
                                                data-testid={`unarchive-order-${o.id}`}
                                            >
                                                <ArchiveRestore size={14} /> Désarchiver
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="bc-btn bc-btn--sm bc-btn--ghost"
                                                onClick={(e) => { e.stopPropagation(); archive.mutate(o.id); }}
                                                title="Archiver cette commande"
                                                data-testid={`archive-order-${o.id}`}
                                            >
                                                <Archive size={14} /> Archiver
                                            </button>
                                        )}
                                        <select
                                            className="bc-input"
                                            style={{ padding: "0.5rem 0.75rem", fontSize: "0.8rem" }}
                                            value={o.status}
                                            onChange={(e) => updateStatus.mutate({ id: o.id, status: e.target.value })}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`order-status-select-${o.id}`}
                                        >
                                            {ORDER_STATUSES.map((s) => (
                                                <option key={s} value={s}>
                                                    {ORDER_STATUS_LABELS[s]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {open && (
                                <div className="mt-6 pt-4 border-t grid grid-cols-1 md:grid-cols-[1fr,220px] gap-6" style={{ borderColor: "var(--bc-border)" }}>
                                    <div>
                                        <p className="bc-label-small mb-3">Articles</p>
                                        <div className="space-y-2">
                                            {o.items.map((it, idx) => (
                                                <div key={idx} className="flex justify-between items-baseline text-sm gap-4">
                                                    <div>
                                                        <span className="text-[#F2ECE0]">{it.article}</span>
                                                        <span className="text-[10px] text-[#A0A0A0] ml-2 uppercase tracking-widest">
                                                            {it.code || "—"}
                                                        </span>
                                                    </div>
                                                    <div className="text-right whitespace-nowrap">
                                                        <span className="text-[#A0A0A0]">
                                                            {it.quantity} {it.unite || "kg"} ×{" "}
                                                            {it.prix_kg_ht != null ? `CHF ${Number(it.prix_kg_ht).toFixed(2)}` : "—"}
                                                        </span>
                                                        <span className="text-[#D4BC7C] ml-3 font-medium">
                                                            {it.line_total != null ? formatCHF(it.line_total) : "Sur demande"}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {o.notes && (
                                            <div className="mt-5">
                                                <p className="bc-label-small mb-1">Notes du client</p>
                                                <p className="text-sm text-[#F2ECE0]/85 whitespace-pre-line">{o.notes}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-sm space-y-2">
                                        <p className="bc-label-small mb-2">Contact</p>
                                        <p className="text-[#F2ECE0]">{o.user_email}</p>
                                        <p className="text-[#A0A0A0]">{o.user_telephone}</p>
                                    </div>
                                </div>
                            )}

                            {open && o.review && (
                                <div
                                    className="mt-4 p-4 border"
                                    style={{
                                        borderColor: o.review.has_complaint && o.review.complaint_status !== "resolved" ? "#fca5a5" : "var(--bc-gold)",
                                        background: o.review.has_complaint && o.review.complaint_status !== "resolved"
                                            ? "rgba(252,165,165,0.05)"
                                            : "rgba(212,188,124,0.04)",
                                    }}
                                    data-testid={`admin-order-review-${o.id}`}
                                >
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <div>
                                            <p className="bc-label-small mb-1">Évaluation du client</p>
                                            <ReviewDisplay review={o.review} />
                                        </div>
                                        {o.review.has_complaint && o.review.complaint_status !== "resolved" && (
                                            <button
                                                className="bc-btn bc-btn--sm"
                                                onClick={() => {
                                                    const note = window.prompt("Note interne sur la résolution (optionnel) :", "");
                                                    if (note !== null) resolveComplaint.mutate({ id: o.id, note });
                                                }}
                                                data-testid={`resolve-complaint-${o.id}`}
                                            >
                                                <Check size={14} /> Marquer résolue
                                            </button>
                                        )}
                                    </div>
                                    {o.review.comment && (
                                        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bc-border)" }}>
                                            <p className="text-[10px] text-[#A0A0A0] uppercase tracking-widest mb-1">Commentaire</p>
                                            <p className="text-sm text-[#F2ECE0]/85 whitespace-pre-line">{o.review.comment}</p>
                                        </div>
                                    )}
                                    {o.review.has_complaint && o.review.complaint_text && (
                                        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bc-border)" }}>
                                            <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#fca5a5" }}>Problème signalé</p>
                                            <p className="text-sm text-[#F2ECE0]/85 whitespace-pre-line">{o.review.complaint_text}</p>
                                        </div>
                                    )}
                                    {o.review.resolution_note && (
                                        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bc-border)" }}>
                                            <p className="text-[10px] text-[#D4BC7C] uppercase tracking-widest mb-1">Résolution</p>
                                            <p className="text-sm text-[#F2ECE0]/85 whitespace-pre-line">{o.review.resolution_note}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
